import { Button } from '@talentmesh/ui/components/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@talentmesh/ui/components/dropdown-menu';
import { MoreVerticalIcon } from 'lucide-react';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { DialogEnum, useActionDialogContext } from '../../../../components/dialogs/Context/ActionDialogContext';
import { useCandidatesOverviewContext } from '../../CandidatesOverviewContext/CandidatesOverviewContext';
import downloadBlob from '../utils/downloadBlob';
import { useAssessmentClient, useBlobClient, useCandidatesClient } from '@/Hooks/ClientHooks';
import useMap from '@/Hooks/useMap';
import { ApplicantRecruitmentOverviewResponse } from '@/Models/CandidateOverview';
import { mapFromCandidateOverviewToCandidateDialogModel } from '@/Pages/Dialogs/Dialogs.Utils';
import isDownloadCVAllowed from '@/Utils/isDownloadCVAllowed';
import isFullReportDownloadDisabled from '@/Utils/isFullReportDownloadAllowed';
import isResendInvitationAllowed from '@/Utils/isResendInvitationAllowed';
import isResetAssessmentAllowedState from '@/Utils/isResetAssessmentAllowedState';
import UIStrings from '@/Utils/UIStrings';

type DownloadItemType = 'cv' | 'report';

type MoreMenuItem = {
    key: string;
    menuItemLabel: string;
    disabled?: boolean;
    onSelect: () => void | Promise<void>;
};

interface MoreActionsCellProps {
    candidate: ApplicantRecruitmentOverviewResponse;
}

function MoreActionsCell({ candidate }: MoreActionsCellProps) {
    const { showDialog } = useActionDialogContext();
    const { candidateId, candidateFirstName, candidateLastName, applicantStatus, completedAt, talentScore } = candidate;
    const { assessmentId } = useCandidatesOverviewContext();
    const [loadingMap, actLoadingMap] = useMap<DownloadItemType, string>();
    const blobClient = useBlobClient();
    const candidatesClient = useCandidatesClient();
    const assessmentClient = useAssessmentClient();

    const menuItems: MoreMenuItem[] = useMemo(() => {
        const items: MoreMenuItem[] = [];

        if (isResendInvitationAllowed(applicantStatus, completedAt)) {
            items.push({
                key: 'resend',
                menuItemLabel: UIStrings.SendAssessmentInvitation,
                onSelect: async () => {
                    try {
                        await candidatesClient.resendInvitationAsync(assessmentId, candidateId);
                        toast.success(UIStrings.InvitationHasBeenSentTo(candidateFirstName, candidateLastName));
                    } catch {
                        toast.error(UIStrings.SomethingWentWrongPleaseTryAgainLater);
                    }
                },
            });
        }

        if (isDownloadCVAllowed(applicantStatus)) {
            items.push({
                key: 'cv',
                menuItemLabel: UIStrings.DownloadCv,
                disabled: loadingMap.has('cv'),
                onSelect: async () => {
                    if (loadingMap.has('cv')) {
                        return;
                    }

                    actLoadingMap.set('cv', 'true');

                    toast.promise(
                        async () => {
                            const { blob, filename } = await blobClient.downloadCvAsync(assessmentId, candidateId);
                            downloadBlob(filename, blob);
                        },
                        {
                            loading: UIStrings.FileDownloadPending(candidateFirstName, UIStrings.CV),
                            success: {
                                message: UIStrings.DownloadSuccessful,
                                description: UIStrings.FileHasBeenDownloadedSuccessfully(UIStrings.CV),
                            },
                            error: {
                                message: UIStrings.DownloadFailed,
                                description: UIStrings.SorryWeWereUnableToDownloadApplicantsFile(UIStrings.CV),
                            },
                        },
                    );

                    actLoadingMap.remove('cv');
                },
            });
        }

        if (isResetAssessmentAllowedState(applicantStatus)) {
            items.push({
                key: 'reset',
                menuItemLabel: UIStrings.ResetAssessment,
                onSelect: () =>
                    showDialog(
                        [mapFromCandidateOverviewToCandidateDialogModel(assessmentId, candidate)],
                        DialogEnum.ResetAssessment,
                    ),
            });
        }

        if (!isFullReportDownloadDisabled(talentScore)) {
            items.push({
                key: 'report',
                menuItemLabel: UIStrings.DownloadReport,
                disabled: loadingMap.has('report'),
                onSelect: async () => {
                    if (loadingMap.has('report')) {
                        return;
                    }

                    actLoadingMap.set('report', 'true');

                    toast.promise(
                        async () => {
                            const candidateIdBase64 = btoa(candidateId);
                            const blob = await assessmentClient.getCandidateResultsPdfReport(
                                assessmentId,
                                candidateIdBase64,
                            );

                            downloadBlob(
                                UIStrings.FormFileName(
                                    `${candidateFirstName} ${candidateLastName}`,
                                    UIStrings.Report,
                                    '.pdf',
                                ),
                                blob,
                            );
                        },
                        {
                            loading: UIStrings.FileDownloadPending(candidateFirstName, UIStrings.Report),
                            success: {
                                message: UIStrings.DownloadSuccessful,
                                description: UIStrings.FileHasBeenDownloadedSuccessfully(UIStrings.Report),
                            },
                            error: {
                                message: UIStrings.DownloadFailed,
                                description: UIStrings.SorryWeWereUnableToDownloadApplicantsFile(UIStrings.Report),
                            },
                        },
                    );

                    actLoadingMap.remove('report');
                },
            });
        }

        return items;
    }, [talentScore, applicantStatus, completedAt, loadingMap]);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon-xs"
                    type="button"
                    disabled={menuItems.length === 0}
                    className="mx-auto"
                >
                    <MoreVerticalIcon className="size-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-cy="moreActionsMenu" className="min-w-56">
                <DropdownMenuGroup>
                    {menuItems.map((item) => (
                        <DropdownMenuItem
                            key={item.key}
                            disabled={item.disabled}
                            data-cy={item.key}
                            onSelect={() => item.onSelect()}
                        >
                            {item.menuItemLabel}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default MoreActionsCell;
